'use client'

import { redactWsUrl, selectBrowserSocketUrl } from '@/lib/compiler/same-origin-ws'

/**
 * Resolve a backend WebSocket URL, attaching auth via query param when a session cookie exists.
 * The token is read server-side in /api/auth/ws-url — never exposed to client JS.
 * A failed request, or a response that points a public page at another host, keeps the
 * page-origin URL. Loopback development can still receive the compiler host.
 */
export async function resolveAuthenticatedWsUrl(wsUrl: string): Promise<string> {
  try {
    const res = await fetch('/api/auth/ws-url', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: wsUrl }),
    })

    if (!res.ok) {
      console.warn('[ws-url] resolver rejected the socket URL', { status: res.status })
      return wsUrl
    }

    const data = (await res.json()) as { url?: string }
    const selected = selectBrowserSocketUrl(wsUrl, data.url?.trim())
    if (selected.rejectedForeignHost) {
      console.warn('[ws-url] ignored a socket URL on a different host', {
        requested: redactWsUrl(wsUrl),
        resolved: redactWsUrl(data.url ?? ''),
      })
    }
    return selected.url
  } catch {
    console.warn('[ws-url] resolver request failed; using the page-origin socket URL')
    return wsUrl
  }
}
